import { useState, useMemo, useCallback, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { UserViolation, ViolationQueryState } from '../types/Violation'
import { useViolationContext } from '../contexts/ViolationContext'
import { formatDate } from '../utils/formatDate'
import { usePersistedState } from '../hooks/usePersistedState'
import { generateColumns } from '../utils/tableUtils'
import DataTable from './common/DataTable'
import { AlertView } from './common/Alert'
import { ColumnDef, SortingFn } from '@tanstack/react-table'
import { useCSVDownload } from '../hooks/useCSVDownload'
import { Spinner } from './ui/spinner'
import { RefreshCw } from 'lucide-react'
import { Download } from 'lucide-react'
import { DataTableSkeleton } from './TableSkeleton'
import { useTrackNavigation } from '../hooks/useTrackNavigation'
import { getDateRangeError, isCalendarDate } from '../utils/dateRange'

type ViolationFilters = Pick<ViolationQueryState, 'startDate' | 'endDate'>

// Paging and the table's own text filter never change which violations the API
// returned, so only the date range decides whether the form has drifted from
// the submitted search.
const sameFilters = (left: ViolationFilters, right: ViolationFilters): boolean =>
  left.startDate === right.startDate && left.endDate === right.endDate

const isStoredQueryState = (value: unknown): value is ViolationQueryState => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const state = value as ViolationQueryState
  return (state.startDate === '' || isCalendarDate(state.startDate)) &&
    (state.endDate === '' || isCalendarDate(state.endDate)) &&
    Number.isSafeInteger(state.currentPage) && state.currentPage > 0 &&
    [10, 50].includes(state.itemsPerPage) && typeof state.tableFilter === 'string' &&
    typeof state.searchTerm === 'string' && typeof state.sortField === 'string' &&
    ['asc', 'desc'].includes(state.sortDirection) && Array.isArray(state.filteredUsers)
}

const countFields = ['In Corridor', 'Completed Trips', 'violations']
const sortCount: SortingFn<UserViolation> = (left, right, columnId) => {
  const a = Number(left.getValue(columnId))
  const b = Number(right.getValue(columnId))
  if (!Number.isFinite(a)) return Number.isFinite(b) ? 1 : 0
  if (!Number.isFinite(b)) return -1
  return a - b
}

const ViolationsComponent = (): JSX.Element => {
  const { data: users, submittedQuery, loading, error, fetchData: fetchUsers, clearData: clearUsers } = useViolationContext()
  const [filteredUsers, setFilteredUsers] = useState<UserViolation[]>(users)
  const { isDownloading, handleDownload } = useCSVDownload<UserViolation>()
  useTrackNavigation()

  useEffect(() => {
    if (users.length === 0) setFilteredUsers([])
  }, [users])

  const getDefaultState = (): ViolationQueryState => {
    const defaultEndDate = new Date()
    const defaultStartDate = new Date(defaultEndDate)
    defaultStartDate.setDate(defaultStartDate.getDate() - 2)

    return {
      startDate: formatDate(defaultStartDate),
      endDate: formatDate(defaultEndDate),
      searchTerm: '',
      currentPage: 1,
      itemsPerPage: 10,
      sortField: '',
      sortDirection: 'asc',
      filteredUsers: [],
      tableFilter: '',
    }
  }

  const [queryState, setQueryState] = usePersistedState<ViolationQueryState>({
    storageKey: 'ViolationState',
    defaultValue: getDefaultState(),
    validate: isStoredQueryState,
  })
  const [showUnappliedFiltersHint, setShowUnappliedFiltersHint] = useState(false)
  const [dateRangeError, setDateRangeError] = useState<string | null>(null)
  const hasUnappliedFilters = users.length > 0 && (!submittedQuery || !sameFilters(queryState, submittedQuery))
  const canDownload = !loading && !error && users.length > 0 && filteredUsers.length > 0

  // Any later edit — Find included — hides the hint until Download CSV is
  // pressed again.
  useEffect(() => {
    setShowUnappliedFiltersHint(false)
  }, [queryState.startDate, queryState.endDate])

  // The message describes the dates that were on screen when Find was pressed,
  // so any edit to either one retires it.
  useEffect(() => {
    setDateRangeError(null)
  }, [queryState.startDate, queryState.endDate])

  const setTableFilter = useCallback((tableFilter: string) =>
    setQueryState(prev => ({ ...prev, tableFilter, currentPage: 1 })),
    [setQueryState]
  )

  const setCurrentPage = useCallback((currentPage: number) =>
    setQueryState(prev => ({ ...prev, currentPage })),
    [setQueryState]
  )

  const setItemsPerPage = (itemsPerPage: number) => {
    setQueryState(prev => ({ ...prev, itemsPerPage, currentPage: 1 }))
  }

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setQueryState(prev => ({ ...prev, startDate: e.target.value }))

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setQueryState(prev => ({ ...prev, endDate: e.target.value }))

  const resetToDefaults = () => {
    setQueryState(getDefaultState())
    setShowUnappliedFiltersHint(false)
    setDateRangeError(null)
    clearUsers()
  }

  const handleDownloadCSV = () => {
    if (!canDownload || isDownloading) return
    // The rows on screen belong to the submitted date range, so a pending edit
    // would silently export the previous search.
    if (hasUnappliedFilters) {
      setShowUnappliedFiltersHint(true)
      return
    }
    handleDownload({
      getData: () => filteredUsers,
      filename: 'user_data.csv',
      sanitizeData: (users) => users.map(({ user_id, ...rest }) => rest)
    })
  }

  const handleDataChange = useCallback((data: UserViolation[]) => {
    setFilteredUsers(data)
  }, [setFilteredUsers])

  const submitSearch = useCallback((filters: ViolationFilters) => {
    setDateRangeError(null)
    setShowUnappliedFiltersHint(false)
    void fetchUsers(filters)
    setQueryState(prev => ({
      ...prev,
      tableFilter: '',
      currentPage: 1,
    }))
  }, [fetchUsers, setQueryState])

  const handleFindClick = () => {
    const validationError = getDateRangeError(queryState.startDate, queryState.endDate)
    setDateRangeError(validationError)
    if (validationError) return
    submitSearch({ startDate: queryState.startDate, endDate: queryState.endDate })
  }

  const columns: ColumnDef<UserViolation, any>[] = useMemo(() => {
    const defaultColumns: ColumnDef<UserViolation, any>[] = [
      { accessorKey: 'user_uuid', header: 'User UUID' },
      { accessorKey: 'In Corridor', header: 'In Corridor' },
      { accessorKey: 'Completed Trips', header: 'Completed Trips' },
      { accessorKey: 'violations', header: 'Violations' },
    ]

    if (!users.length) return defaultColumns

    return generateColumns<UserViolation>(users).map(column =>
      'accessorKey' in column && countFields.includes(String(column.accessorKey))
        ? { ...column, sortingFn: sortCount, sortDescFirst: false }
        : column
    )
  }, [users])

  const paginationState = {
    currentPage: queryState.currentPage,
    itemsPerPage: queryState.itemsPerPage,
    onPageChange: setCurrentPage,
    onItemsPerPageChange: setItemsPerPage
  }

  const tableFilterState = {
    tableFilter: queryState.tableFilter,
    onTableFilterChange: setTableFilter
  }

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row gap-5 mb-4">
        <Card className="flex-1 md:max-w-xl bg-white">
          <CardHeader>
            <CardTitle>Trip Violations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 space-y-1">
                <label htmlFor="startDate" className="text-sm font-medium">Start Date</label>
                <Input
                  id="startDate"
                  type="date"
                  value={queryState.startDate}
                  onChange={handleStartDateChange}
                  aria-invalid={Boolean(dateRangeError)}
                  aria-describedby={dateRangeError ? 'violation-date-error' : undefined}
                />
              </div>
              <div className="flex-1 space-y-1">
                <label htmlFor="endDate" className="text-sm font-medium">End Date</label>
                <Input
                  id="endDate"
                  type="date"
                  value={queryState.endDate}
                  onChange={handleEndDateChange}
                  aria-invalid={Boolean(dateRangeError)}
                  aria-describedby={dateRangeError ? 'violation-date-error' : undefined}
                />
              </div>
              <Button
                onClick={handleFindClick}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Find
              </Button>
            </div>
            {dateRangeError && (
              <p id="violation-date-error" className="text-red-500 text-sm mt-2" role="alert" data-testid="date-range-error">
                {dateRangeError}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-2 md:justify-end self-end">
          <Button
            onClick={resetToDefaults}
            variant="outline"
            className="w-full md:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground bg-green-600 text-white hover:bg-green-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleDownloadCSV}
            variant="outline"
            className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground bg-green-600 text-white hover:bg-green-700"
            disabled={isDownloading || !canDownload}
            title={hasUnappliedFilters ? "Press Find to apply the new filters" : undefined}
            data-testid="download-csv-button"
          >
            {isDownloading ? (
              <>
                <Spinner size="small" className="mr-2" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </>
            )}
          </Button>
          {hasUnappliedFilters && showUnappliedFiltersHint && (
            <p className="text-sm text-amber-700" role="status" data-testid="unapplied-filters-hint">
              Filters changed — press Find to refresh the results before downloading.
            </p>
          )}
        </div>
      </div>

      {error ? (
        <AlertView
          message={error}
          onAction={() => submittedQuery && submitSearch(submittedQuery)}
          buttonTitle="Retry"
        />
      ) : loading ? (
        <DataTableSkeleton rows={10} columns={4} />
      ) : (
        <DataTable
          data={users}
          columns={columns}
          paginationState={paginationState}
          onDataChange={handleDataChange}
          tableFilterState={tableFilterState}
        />
      )}
    </div>
  )
}

export default ViolationsComponent
